<template>
  <div
    class="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded"
    style="height: 450px; overflow: hidden;"
  >
    <div class="rounded-t mb-0 px-4 py-3 border-0">
      <div class="flex flex-wrap items-center">
        <div class="relative px-2 py-1">
          <h3 class="font-semibold text-base text-blueGray-700">
            일별 수집 트렌드
          </h3>
        </div>
      </div>
    </div>
    <div class="p-4 flex-auto" style="overflow: hidden;">
      <div v-if="!projectDailyStats || projectDailyStats.length === 0" class="flex items-center justify-center" style="height: 400px;">
        <p class="text-blueGray-400">데이터를 불러오는 중...</p>
      </div>
      <div v-else>
        <div class="relative" style="height: 330px;">
          <canvas ref="dailyTrendCanvas"></canvas>
        </div>
        <div class="mt-2 text-xs text-blueGray-400 text-center">
          <i class="mdi mdi-information-outline"></i> Ctrl+휠로 확대/축소, 드래그로 이동
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
);

export default {
  name: 'card-project-daily-chart',
  props: {
    projectDailyStats: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      chart: null
    };
  },
  computed: {
    sortedData() {
      if (!this.projectDailyStats || this.projectDailyStats.length === 0) {
        return [];
      }

      // Sort by date ascending for chart
      return [...this.projectDailyStats].sort((a, b) => {
        return new Date(a.collection_date) - new Date(b.collection_date);
      });
    }
  },
  watch: {
    projectDailyStats: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.$nextTick(() => {
            if (this.chart) {
              this.updateChart();
            } else {
              this.initChart();
            }
          });
        }
      },
      deep: true
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (this.projectDailyStats && this.projectDailyStats.length > 0) {
        this.initChart();
      }
    });
  },
  beforeUnmount() {
    if (this.chart) {
      this.chart.destroy();
    }
  },
  methods: {
    initChart() {
      // 기존 차트가 있으면 먼저 파괴
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }

      const ctx = this.$refs.dailyTrendCanvas;
      if (!ctx) return;

      if (!this.sortedData || this.sortedData.length === 0) return;

      const labels = this.sortedData.map(item => item.collection_date);

      const datasets = [
        {
          label: '언론',
          data: this.sortedData.map(item => Number(item.press_count) || 0),
          borderColor: '#FF6384',
          backgroundColor: 'rgba(255, 99, 132, 0.1)',
          borderWidth: 2,
          tension: 0.4,
          pointRadius: 1,
          pointHoverRadius: 3,
          pointBackgroundColor: '#FF6384'
        },
        {
          label: '블로그',
          data: this.sortedData.map(item => Number(item.blog_count) || 0),
          borderColor: '#36A2EB',
          backgroundColor: 'rgba(54, 162, 235, 0.1)',
          borderWidth: 2,
          tension: 0.4,
          pointRadius: 1,
          pointHoverRadius: 3,
          pointBackgroundColor: '#36A2EB'
        },
        {
          label: '지식인',
          data: this.sortedData.map(item => Number(item.kin_count) || 0),
          borderColor: '#FFCE56',
          backgroundColor: 'rgba(255, 206, 86, 0.1)',
          borderWidth: 2,
          tension: 0.4,
          pointRadius: 1,
          pointHoverRadius: 3,
          pointBackgroundColor: '#FFCE56'
        },
        {
          label: '카페',
          data: this.sortedData.map(item => Number(item.cafe_count) || 0),
          borderColor: '#4BC0C0',
          backgroundColor: 'rgba(75, 192, 192, 0.1)',
          borderWidth: 2,
          tension: 0.4,
          pointRadius: 1,
          pointHoverRadius: 3,
          pointBackgroundColor: '#4BC0C0'
        },
        {
          label: '커뮤니티',
          data: this.sortedData.map(item => Number(item.community_count) || 0),
          borderColor: '#9966FF',
          backgroundColor: 'rgba(153, 102, 255, 0.1)',
          borderWidth: 2,
          tension: 0.4,
          pointRadius: 1,
          pointHoverRadius: 3,
          pointBackgroundColor: '#9966FF'
        },
        {
          label: '유튜브',
          data: this.sortedData.map(item => Number(item.youtube_count) || 0),
          borderColor: '#FF9F40',
          backgroundColor: 'rgba(255, 159, 64, 0.1)',
          borderWidth: 2,
          tension: 0.4,
          pointRadius: 1,
          pointHoverRadius: 3,
          pointBackgroundColor: '#FF9F40'
        }
      ];

      const config = {
        type: 'line',
        data: {
          labels: labels,
          datasets: datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false
          },
          plugins: {
            legend: {
              position: 'top',
              labels: {
                padding: 8,
                font: {
                  size: 10
                },
                usePointStyle: true,
                boxWidth: 8,
                boxHeight: 8
              }
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: function(context) {
                  let label = context.dataset.label || '';
                  if (label) {
                    label += ': ';
                  }
                  label += context.parsed.y.toLocaleString() + '건';
                  return label;
                }
              }
            },
            zoom: {
              zoom: {
                wheel: {
                  enabled: true,
                  modifierKey: 'ctrl'
                },
                pinch: {
                  enabled: true
                },
                mode: 'x'
              },
              pan: {
                enabled: true,
                mode: 'x'
              }
            }
          },
          scales: {
            x: {
              display: true,
              title: {
                display: false
              },
              ticks: {
                font: {
                  size: 9
                },
                maxRotation: 45,
                minRotation: 45,
                autoSkip: true,
                autoSkipPadding: 30,
                maxTicksLimit: 12
              }
            },
            y: {
              display: true,
              title: {
                display: false
              },
              beginAtZero: true,
              ticks: {
                font: {
                  size: 9
                },
                callback: function(value) {
                  return value.toLocaleString();
                }
              }
            }
          }
        }
      };

      this.chart = new ChartJS(ctx, config);
    },
    updateChart() {
      if (!this.chart) return;

      const labels = this.sortedData.map(item => item.collection_date);

      this.chart.data.labels = labels;
      this.chart.data.datasets[0].data = this.sortedData.map(item => Number(item.press_count) || 0);
      this.chart.data.datasets[1].data = this.sortedData.map(item => Number(item.blog_count) || 0);
      this.chart.data.datasets[2].data = this.sortedData.map(item => Number(item.kin_count) || 0);
      this.chart.data.datasets[3].data = this.sortedData.map(item => Number(item.cafe_count) || 0);
      this.chart.data.datasets[4].data = this.sortedData.map(item => Number(item.community_count) || 0);
      this.chart.data.datasets[5].data = this.sortedData.map(item => Number(item.youtube_count) || 0);

      this.chart.resetZoom();
      this.chart.update();
    }
  }
};
</script>
